import React from 'react';
import axios from 'axios';
import { pick } from 'lodash';
import classnames from 'classnames';
import PropTypes from 'prop-types';
import { connect } from 'react-redux';
import { withStyles } from 'theme/withStyles';
import dataActions from 'core/data/actions';
import groupActions from 'core/groups/actions';
import RawHtml from 'common/RawHtml';
import { PAGES_ROOT } from 'config/api';
import hasError from 'utils/hasError';

const styles = ({ typography }) => ({
  applePay: {
    '-apple-pay-button-style': 'black',
    '-apple-pay-button-type': 'plain',
    '-webkit-appearance': '-apple-pay-button',
    height: 40,
  },
  buttonWrap: {
    '& small': {
      ...typography.p,
      ...typography.p3,
    },
  },
  disabled: {
    opacity: '50%',
    pointerEvents: 'none',
  },
});

/**
 * https://developer.apple.com/documentation/apple_pay_on_the_web
 */
export const ApplePayBare = ({
  changeData,
  classes,
  config,
  disabled,
  groupData,
  groupName,
  nextGroup,
  options,
  total,
}) => {
  const {
    merchantCapabilities = ['supports3DS'],
    countryCode = 'US',
    currencyCode = 'USD',
    merchantDisplayName = 'Example Merchant',
    supportedNetworks = ['AMEX', 'DISCOVER', 'JCB', 'MASTERCARD', 'VISA'],
  } = config;

  const structuredConfig = {
    countryCode,
    currencyCode,
    merchantCapabilities,
    supportedNetworks,
    total: {
      label: merchantDisplayName,
      total,
    },
  };

  function handleOnValidateMerchant(e, applePaySession) {
    axios
      .post(`${PAGES_ROOT}/funding/groups/FUNDING_AMOUNT/actions/validateApplePay`, {
        appRequest_mobileWallet_applePayUrl: e.validationURL,
      })
      .then((rs) => {
        applePaySession.completeMerchantValidation(
          JSON.parse(rs.data.data.appRequest_mobileWallet_applePayMerchantSession),
        );
      })
      .catch(() => {
        applePaySession.completeMerchantValidation({});
      });
  }

  function handleOnPaymentAuthorized(e, applePaySession) {
    const {
      payment: {
        token: { paymentData },
      },
    } = e;

    changeData('appRequest_mobileWallet_encryptedPayment', btoa(JSON.stringify(paymentData)));
    changeData('appRequest_mobileWalletMethod', 'APPLE_PAY');
    changeData('appRequest_fundingMethod', 'MOBILE_WALLET');

    try {
      nextGroup(groupName, {
        ...groupData,
        appRequest_fundingMethod: 'MOBILE_WALLET',
        appRequest_mobileWalletMethod: 'APPLE_PAY',
        appRequest_mobileWallet_encryptedPayment: btoa(JSON.stringify(paymentData)),
      });

      applePaySession.completePayment(window.ApplePaySession.STATUS_SUCCESS);
    } catch (error) {
      window.console.log(error);
      applePaySession.completePayment(window.ApplePaySession.STATUS_FAILURE);
    }
  }

  function handleBtnApplePayClick() {
    if (window.ApplePaySession && !disabled) {
      structuredConfig.total.amount = total;

      const session = new window.ApplePaySession(3, structuredConfig);

      session.onvalidatemerchant = (e) => handleOnValidateMerchant(e, session);
      session.onpaymentauthorized = (e) => handleOnPaymentAuthorized(e, session);

      if (session) {
        session.begin();
      }
    }
  }

  return (
    <>
      {total !== '0' && (
        <div
          className={classnames('region-apple-pay', classes.buttonWrap, {
            [classes.disabled]: disabled,
            'disable-mobile-wallet': disabled,
          })}
        >
          <div
            className={classnames('apple-pay-button apple-pay-button-black', classes.applePay)}
            onClick={handleBtnApplePayClick}
          />
          {options.labelDescr && <RawHtml type="small">{options.labelDescr}</RawHtml>}
        </div>
      )}
    </>
  );
};

ApplePayBare.propTypes = {
  changeData: PropTypes.func.isRequired,
  config: PropTypes.shape({
    countryCode: PropTypes.string,
    currencyCode: PropTypes.string,
    merchantCapabilities: PropTypes.string,
    merchantDisplayName: PropTypes.string,
    supportedNetworks: PropTypes.string,
  }).isRequired,
  disabled: PropTypes.bool,
  groupData: PropTypes.shape({}).isRequired,
  groupName: PropTypes.string.isRequired,
  nextGroup: PropTypes.func.isRequired,
  options: PropTypes.shape({
    labelDescr: PropTypes.string,
  }).isRequired,
  total: PropTypes.string,
};

ApplePayBare.defaultProps = {
  disabled: false,
  total: String(0),
};

const mapStateToProps = ({ configs, data, fields: stateFields, groups, ui }) => {
  const {
    FUNDING_AMOUNT: { fields },
  } = groups;
  const group = groups[ui.activeGroup];

  return {
    config: {
      ...configs.mobileWallet,
      ...configs.applePay,
    },
    disabled: hasError(pick(stateFields, group.fields)) || !data.appRequest_totalFundingAmount,
    groupData:
      fields &&
      fields.reduce(
        (groupData, field) => ({
          ...groupData,
          [field]: data[field],
        }),
        {},
      ),
    groupName: groups.FUNDING_AMOUNT.name,
    total: String(data.appRequest_totalFundingAmount),
  };
};

const mapDispatchToProps = {
  changeData: dataActions.changeData,
  nextGroup: groupActions.next,
};
const styledApplePay = withStyles(styles)(ApplePayBare);

export default connect(mapStateToProps, mapDispatchToProps)(styledApplePay);
